'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Product } from './types'

interface QuoteCartItem {
  product: Product
  quantity: number
  addedAt: Date
  notes?: string
}

interface QuoteCartContextType {
  quoteItems: QuoteCartItem[]
  addToQuoteCart: (product: Product, quantity?: number, notes?: string) => void
  removeFromQuoteCart: (productId: string) => void
  updateQuantity: (productId: string, quantity: number) => void
  updateNotes: (productId: string, notes: string) => void
  clearQuoteCart: () => void
  getItemCount: () => number
  getTotalQuantity: () => number
  isInQuoteCart: (productId: string) => boolean
  getCartItem: (productId: string) => QuoteCartItem | undefined
}

const QuoteCartContext = createContext<QuoteCartContextType | undefined>(undefined)

const STORAGE_KEY = 'prosoft-quote-cart'

export function QuoteCartProvider({ children }: { children: ReactNode }) {
  const [quoteItems, setQuoteItems] = useState<QuoteCartItem[]>([])
  const [isHydrated, setIsHydrated] = useState(false)

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      console.log('Loading quote cart from localStorage:', stored ? 'found' : 'empty')
      if (stored) {
        const parsed = JSON.parse(stored)
        // Convert date strings back to Date objects
        const items = parsed.map((item: any) => ({
          ...item,
          addedAt: new Date(item.addedAt)
        }))
        console.log('Loaded', items.length, 'items from localStorage')
        setQuoteItems(items)
      }
    } catch (error) {
      console.error('Failed to load quote cart from localStorage:', error)
    } finally {
      setIsHydrated(true)
    }
  }, [])

  // Save to localStorage whenever items change (after hydration)
  useEffect(() => {
    if (isHydrated) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(quoteItems))
      } catch (error) {
        console.error('Failed to save quote cart to localStorage:', error)
      }
    }
  }, [quoteItems, isHydrated])

  const addToQuoteCart = (product: Product, quantity: number = 1, notes?: string) => {
    console.log('Adding to quote cart:', product.partNumber, quantity)
    setQuoteItems(prev => {
      // Check if product already exists in cart
      const existingIndex = prev.findIndex(item => item.product.id === product.id)
      
      if (existingIndex !== -1) {
        // Update existing item quantity
        const updated = [...prev]
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
          notes: notes || updated[existingIndex].notes
        }
        console.log('Updated existing item, new cart:', updated.length, 'items')
        return updated
      } else {
        // Add new item
        const newItems = [...prev, {
          product,
          quantity,
          addedAt: new Date(),
          notes
        }]
        console.log('Added new item, new cart:', newItems.length, 'items')
        return newItems
      }
    })
  }

  const removeFromQuoteCart = (productId: string) => {
    setQuoteItems(prev => prev.filter(item => item.product.id !== productId))
  }

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromQuoteCart(productId)
      return
    }

    setQuoteItems(prev => 
      prev.map(item => 
        item.product.id === productId 
          ? { ...item, quantity }
          : item
      )
    )
  }

  const updateNotes = (productId: string, notes: string) => {
    setQuoteItems(prev => 
      prev.map(item => 
        item.product.id === productId 
          ? { ...item, notes }
          : item
      )
    )
  }

  const clearQuoteCart = () => {
    setQuoteItems([])
  }

  const getItemCount = () => {
    return quoteItems.length
  }

  const getTotalQuantity = () => {
    return quoteItems.reduce((total, item) => total + item.quantity, 0)
  }

  const isInQuoteCart = (productId: string) => {
    return quoteItems.some(item => item.product.id === productId)
  }

  const getCartItem = (productId: string) => {
    return quoteItems.find(item => item.product.id === productId)
  }

  return (
    <QuoteCartContext.Provider
      value={{
        quoteItems,
        addToQuoteCart,
        removeFromQuoteCart,
        updateQuantity,
        updateNotes,
        clearQuoteCart,
        getItemCount,
        getTotalQuantity,
        isInQuoteCart,
        getCartItem
      }}
    >
      {children}
    </QuoteCartContext.Provider>
  )
}

export function useQuoteCart() {
  const context = useContext(QuoteCartContext)
  if (context === undefined) {
    throw new Error('useQuoteCart must be used within a QuoteCartProvider')
  }
  return context
}
